/**
 * Who is on what in a project, right now: claimed tasks, claimed plans, roadmap rows and risks,
 * and runs still going, from `GET /projects/v1/:id/working`. One list for the console, the same
 * answer the workbench and the phone show, so two people's teams do not meet by accident.
 */

import { WORKING_KIND_TEXT, type WorkingItem } from '@aioe/shared';
import { Persons } from '@gravity-ui/icons';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { SubjectName } from './SubjectPicker.tsx';
import { Button, ConfirmDialog, Empty, ErrorNote, ListBlock, ListGroup, ListRow, StatusMark } from './ui.tsx';
import { VIEW_TONES } from './ui/Art.tsx';

/** "task T-4 in api · default board", "plan P-2 in web", "roadmap item R-3", "risk RK-4 (Vendor API changes)", "run · planner". */
export function workingWhat(i: WorkingItem): string {
  const where = i.ref.rootId ? ` in ${i.ref.rootId}` : '';
  if (i.kind === 'run') {
    const against = i.task ? `task ${i.task.id}` : i.plan ? `plan ${i.plan.id}${i.plan.step ? ` · ${i.plan.step}` : ''}` : undefined;
    return `${i.actor ?? 'an agent'}${i.teamName || i.teamId ? ` of ${i.teamName ?? i.teamId}` : ''}${where}${against ? ` · ${against}` : ''}`;
  }
  if (i.kind === 'risk') return `risk ${i.ref.id}${i.title ? ` (${i.title})` : ''}${where}`;
  const board = i.kind === 'task' && i.ref.board && i.ref.board !== 'solo' ? ` · ${i.ref.board}` : '';
  return `${WORKING_KIND_TEXT[i.kind]} ${i.ref.id}${where}${board}`;
}

/** How the person's name runs into the item: a risk reads as a sentence ("Ana is on risk RK-4 (Vendor API changes)"), the rest after a dot. */
const workingJoin = (i: WorkingItem): string => (i.kind === 'risk' ? ' is on ' : ' · ');

export function WorkingNow({ projectId, owner, me }: { projectId: string; owner: boolean; me?: string }) {
  const tone = VIEW_TONES.projects!;
  const data = useLoad(() => routes.working(projectId), [projectId], 20_000);
  const [error, setError] = useState<string>();
  const items = data.data?.items ?? [];
  const people = data.data?.people ?? {};
  const [taking, setTaking] = useState<WorkingItem>();
  const release = async (i: WorkingItem) => {
    if (!i.claimId) return;
    setError(undefined);
    try {
      await routes.releaseWorkClaim(projectId, i.claimId);
      data.reload();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <>
    <ListGroup
      title="Working now"
      count={data.data ? items.length || undefined : undefined}
      description="Who is on which task, plan, roadmap item or risk, and which agents are running, across every member's workbench."
      empty={data.data && items.length === 0 && !error ? <Empty tone={tone} icon={<Persons />} title="Nobody is on anything right now" body="When a member's team claims a task, a plan, a roadmap item or a risk, or an agent starts a run, it shows here while it lasts." compact /> : undefined}
    >
      {items.map((i) => (
          <ListRow
            key={`${i.kind}:${i.ref.rootId}:${i.ref.board ?? ''}:${i.ref.id}:${i.sessionId ?? i.claimId ?? ''}`}
            title={
              <>
                {i.principal ? (people[i.principal]?.name ?? <SubjectName id={i.principal} />) : 'An automation'}
                {i.principal === me ? <span className="font-normal text-muted"> (you)</span> : null}
                <span className="font-normal text-muted">
                  {workingJoin(i)}
                  {workingWhat(i)}
                </span>
              </>
            }
            description={`${i.title && i.kind !== 'risk' ? `${i.title} · ` : ''}since ${timeAgo(i.since)}${i.note ? ` · ${i.note}` : ''}`}
            status={<StatusMark state="working" />}
            {...(i.claimId && (i.principal === me || owner)
              ? {
                  compactControl: true,
                  control: (
                    <Button size="sm" variant="ghost" onPress={() => (i.principal === me ? void release(i) : setTaking(i))}>
                      {i.principal === me ? 'Release' : 'Take over'}
                    </Button>
                  ),
                }
              : {})}
          />
      ))}
      {error || data.error ? (
        <ListBlock>
          <ErrorNote error={error ?? data.error} />
        </ListBlock>
      ) : null}
      {!data.data && !data.error ? <ListBlock className="text-sm text-muted">Asking who is on what…</ListBlock> : null}
    </ListGroup>
      <ConfirmDialog
        isOpen={!!taking}
        title={taking ? `Take over ${workingWhat(taking)}` : 'Take over'}
        confirmLabel="Take over"
        onCancel={() => setTaking(undefined)}
        onConfirm={async () => {
          const i = taking;
          setTaking(undefined);
          if (i) await release(i);
        }}
      >
        {taking ? `${people[taking.principal]?.name ?? 'The holder'} is told, and a team of theirs still on it should stop.` : null}
      </ConfirmDialog>
    </>
  );
}
